/**
 * BulkMediaUpload — admin/office tool to drag-drop OR pick an entire folder
 * of photos/videos and attach them to a cleaning task. Uses signed Cloudinary
 * uploads, runs up to 4 concurrent uploads, and shows progress per file.
 */
import { useRef, useState } from "react";
import { Upload, FolderUp, X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { uploadToCloudinary } from "@/components/MediaUpload";

const MAX_PARALLEL = 4;

export default function BulkMediaUpload({ taskId, folder, onUploaded }) {
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [queue, setQueue] = useState([]); // { id, name, size, type, status, progress, error }
    const [busy, setBusy] = useState(false);
    const [dragging, setDragging] = useState(false);

    const addFiles = (fileList) => {
        const arr = Array.from(fileList || []).filter((f) => {
            const isImg = f.type.startsWith("image/");
            const isVid = f.type.startsWith("video/");
            return isImg || isVid;
        });
        if (arr.length === 0) return;
        const items = arr.map((f) => ({
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            file: f,
            name: f.name,
            size: f.size,
            type: f.type.startsWith("video/") ? "video" : "image",
            status: "pending",
            progress: 0,
            error: "",
        }));
        setQueue((q) => [...q, ...items]);
    };

    const removeFromQueue = (id) => {
        setQueue((q) => q.filter((x) => x.id !== id));
    };

    const updateItem = (id, patch) => {
        setQueue((q) => q.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    };

    const startUpload = async () => {
        const pending = queue.filter((x) => x.status === "pending");
        if (pending.length === 0) return;
        setBusy(true);
        try {
            // Process in parallel batches
            for (let i = 0; i < pending.length; i += MAX_PARALLEL) {
                const batch = pending.slice(i, i + MAX_PARALLEL);
                await Promise.all(batch.map(uploadOne));
            }
        } finally {
            setBusy(false);
            onUploaded?.();
        }
    };

    const uploadOne = async (item) => {
        updateItem(item.id, { status: "uploading", progress: 0 });
        try {
            const cloud = await uploadToCloudinary(
                item.file,
                {
                    folder: folder || `zentra/tasks/${taskId || "misc"}`,
                    resourceType: item.type === "video" ? "video" : "image",
                },
                (p) => updateItem(item.id, { progress: p }),
            );
            const payload = {
                type: item.type === "video" ? "video_after" : "photo",
                secure_url: cloud.secure_url,
                public_id: cloud.public_id,
                resource_type: cloud.resource_type,
                point_id: null,
                label: `Subida oficina: ${item.name}`,
                filename: item.name,
                width: cloud.width,
                height: cloud.height,
                duration: cloud.duration,
                bytes: cloud.bytes,
            };
            // Office bulk uploads should NEVER replace the operator's
            // video_before/video_after. The backend dedupes by type for those, so
            // we tag videos as `photo` if there's already a final video, OR use a
            // dedicated "attachment" type. For simplicity, we treat ALL office
            // uploads as photos when not explicitly video_before/_after — keeps
            // mobile operator flow untouched.
            if (item.type === "video") {
                payload.type = "photo";
            }
            await api.post(`/tasks/${taskId}/media`, payload);
            updateItem(item.id, { status: "done", progress: 100 });
        } catch (err) {
            updateItem(item.id, { status: "error", error: formatApiError(err) });
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        // DataTransferItemList supports folder drag with webkitGetAsEntry, but most
        // browsers expose dropped files flat — enough for our use case.
        addFiles(e.dataTransfer.files);
    };

    const totalPending = queue.filter((x) => x.status === "pending").length;
    const totalDone = queue.filter((x) => x.status === "done").length;
    const totalError = queue.filter((x) => x.status === "error").length;

    return (
        <div className="space-y-3" data-testid="bulk-media-upload">
            <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={`rounded-lg border-2 border-dashed transition p-4 ${
                    dragging ? "border-zentra-primary bg-zentra-accent/5" : "border-zentra-border bg-zentra-bg/40"
                }`}
                data-testid="bulk-dropzone"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p className="font-display font-semibold text-zentra-ink text-sm inline-flex items-center gap-2">
                            <Upload size={14} /> Subir fotos y vídeos a esta limpieza
                        </p>
                        <p className="text-xs text-zentra-muted mt-1">
                            Arrastra archivos sueltos o usa los botones para escoger una carpeta entera. Se aceptan imágenes y vídeos.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*,video/*"
                            onChange={(e) => addFiles(e.target.files)}
                            className="hidden"
                            data-testid="bulk-input-files"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-zentra-border bg-white hover:border-zentra-primary"
                            data-testid="bulk-pick-files"
                        >
                            <Upload size={12} /> Archivos sueltos
                        </button>
                        <input
                            ref={folderInputRef}
                            type="file"
                            multiple
                            // @ts-expect-error non-standard but supported in Chromium/Firefox/Edge
                            webkitdirectory=""
                            directory=""
                            onChange={(e) => addFiles(e.target.files)}
                            className="hidden"
                            data-testid="bulk-input-folder"
                        />
                        <button
                            type="button"
                            onClick={() => folderInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-zentra-border bg-white hover:border-zentra-primary"
                            data-testid="bulk-pick-folder"
                        >
                            <FolderUp size={12} /> Carpeta entera
                        </button>
                    </div>
                </div>
            </div>

            {queue.length > 0 && (
                <div className="zentra-card-static p-3 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                        <div className="flex items-center gap-3">
                            <span className="font-display font-semibold text-zentra-ink">
                                {queue.length} archivos en cola
                            </span>
                            <span className="text-zentra-muted">
                                {totalPending} pendientes · {totalDone} subidos · {totalError} errores
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setQueue([])}
                                disabled={busy}
                                className="zentra-btn-ghost text-xs"
                                data-testid="bulk-clear-queue"
                            >
                                Vaciar cola
                            </button>
                            <button
                                type="button"
                                onClick={startUpload}
                                disabled={busy || totalPending === 0}
                                className="zentra-btn-primary text-xs"
                                data-testid="bulk-start-upload"
                            >
                                {busy ? "Subiendo…" : `Subir ${totalPending} archivos`}
                            </button>
                        </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto border border-zentra-border rounded-md divide-y divide-zentra-border">
                        {queue.map((it) => (
                            <div key={it.id} className="px-3 py-2 flex items-center gap-3 text-xs" data-testid={`bulk-row-${it.id}`}>
                                {it.status === "done" ? (
                                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                                ) : it.status === "error" ? (
                                    <AlertCircle size={14} className="text-red-600 shrink-0" />
                                ) : it.status === "uploading" ? (
                                    <Loader2 size={14} className="text-zentra-primary shrink-0 animate-spin" />
                                ) : (
                                    <span className="w-3.5 h-3.5 rounded-full bg-zentra-bg border border-zentra-border shrink-0" />
                                )}
                                <span className="flex-1 truncate text-zentra-ink" title={it.name}>{it.name}</span>
                                <span className="text-zentra-muted shrink-0">{formatBytes(it.size)}</span>
                                {it.status === "uploading" && (
                                    <span className="font-mono-zentra text-zentra-accent-strong shrink-0">{it.progress}%</span>
                                )}
                                {it.status === "error" && (
                                    <span className="text-red-700 truncate max-w-[140px]" title={it.error}>{it.error}</span>
                                )}
                                {it.status === "pending" && (
                                    <button
                                        type="button"
                                        onClick={() => removeFromQueue(it.id)}
                                        className="text-zentra-muted hover:text-red-700 shrink-0"
                                    >
                                        <X size={13} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function formatBytes(b) {
    if (b == null) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
